import { MenuFavoritoModel } from "@/components/layout/menu/models/menu-favorito.model";
import { PermissaoTokenDTO } from "@/core/domain/dto/seguranca/acesso/oauth/permissao-token.dto";
import { SessionService } from "@/core/services/security/session.service";
import { GlobalEvents } from "@/events/global/global.events";
import { inject, Injectable } from "@angular/core";
import { CookieService } from "ngx-cookie-service";
import { BehaviorSubject, Observable } from "rxjs";

const COOKIE_FAVORITOS = "menu.favoritos";

@Injectable({
    providedIn: "root",
})
export class FavoriteMenuService {
    private sessionService = inject(SessionService);
    private cookieService = inject(CookieService);
    private globalEvents = inject(GlobalEvents);

    private readonly favoritosSubject = new BehaviorSubject<Array<MenuFavoritoModel>>(
        this.readFavoritos(),
    );

    readonly favoritos$: Observable<Array<MenuFavoritoModel>> = this.favoritosSubject.asObservable();

    constructor() {
        queueMicrotask(() => this.reload());
        this.globalEvents.sessionRestored$.subscribe(() => this.reload());
    }

    getFavoritos(): Array<MenuFavoritoModel> {
        return this.favoritosSubject.value;
    }

    isFavorite(favoriteId: string): boolean {
        if (!favoriteId) {
            return false;
        }
        return this.getFavoritos().some((item) => item.favoriteId === favoriteId);
    }

    findPermissaoByRole(role: string): PermissaoTokenDTO | undefined {
        const permissoes = this.sessionService.getInstance()?.user?.perfis?.[0]?.permissoes || [];
        return permissoes.find((permissao) => permissao.funcionalidadeRole === role);
    }

    toggleFavorite(params: {
        favoriteId: string;
        role: string;
        label: string;
        route: string;
    }): { added: boolean; removed: boolean; favorito?: MenuFavoritoModel } {
        if (!params.favoriteId || !params.role) {
            return { added: false, removed: false };
        }

        if (this.isFavorite(params.favoriteId)) {
            this.removeFavorite(params.favoriteId);
            return { added: false, removed: true };
        }

        const favorito = this.buildFavorito(params);
        this.addFavorite(favorito);
        return { added: true, removed: false, favorito };
    }

    reload(): void {
        this.favoritosSubject.next(this.readFavoritos());
    }

    private buildFavorito(params: {
        favoriteId: string;
        role: string;
        label: string;
        route: string;
    }): MenuFavoritoModel {
        const permissao = this.findPermissaoByRole(params.role);
        const favorito = new MenuFavoritoModel();
        favorito.favoriteId = params.favoriteId;
        favorito.role = params.role;
        favorito.label = permissao?.funcionalidadeNome || params.label;
        favorito.route = permissao?.funcionalidadeUrl || params.route;
        favorito.icon = permissao?.funcionalidadeIcone || "pi pi-fw pi-star-fill";
        return favorito;
    }

    private addFavorite(favorito: MenuFavoritoModel): void {
        const favoritos = this.getFavoritos().filter(
            (item) => item.favoriteId !== favorito.favoriteId && item.role !== favorito.role,
        );
        favoritos.push(favorito);
        this.persist(favoritos);
    }

    private removeFavorite(favoriteId: string): void {
        const favoritos = this.getFavoritos().filter((item) => item.favoriteId !== favoriteId);
        this.persist(favoritos);
    }

    private persist(favoritos: Array<MenuFavoritoModel>): void {
        const cookieName = this.sessionService.createNameCookieWithDomain(COOKIE_FAVORITOS);
        if (!favoritos.length) {
            this.cookieService.delete(cookieName);
        } else {
            this.cookieService.set(cookieName, JSON.stringify(favoritos));
        }
        this.favoritosSubject.next(favoritos);
    }

    private readFavoritos(): Array<MenuFavoritoModel> {
        const cookieName = this.sessionService.createNameCookieWithDomain(COOKIE_FAVORITOS);
        const raw = this.cookieService.get(cookieName);
        if (!raw) {
            return [];
        }
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }
}
